import { NavLink } from 'react-router-dom';
import { useState } from 'react';
import { Icon, type IconName, LED, Logo } from '@/components/ui';
import { cn } from '@/lib/cn';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useSidebarStore } from '@/stores/sidebarStore';
import { PreferencesModal } from './PreferencesModal';

/**
 * Small separator toggle exposed for the AppShell to render as a sibling
 * of the sidebar, so it can be centered on the vertical separator without
 * being clipped by .side's overflow:hidden.
 */
export function SidebarToggle() {
  const { collapsed, toggle } = useSidebarStore();
  return (
    <button
      type="button"
      onClick={toggle}
      className="sidebar-toggle"
      aria-label={collapsed ? 'Déplier la barre latérale' : 'Réduire la barre latérale'}
      title={collapsed ? 'Déplier' : 'Réduire'}
    >
      <Icon
        name="chev"
        size={12}
        style={{ transform: collapsed ? 'rotate(-90deg)' : 'rotate(90deg)' }}
      />
    </button>
  );
}

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  count?: number;
  badge?: string;
}

/**
 * Left navigation. Brand area = 56 px (same as topbar) with a larger
 * logo + platform name on two UPPERCASE lines + a Lab module badge.
 * The Preferences (settings) button lives in the footer row beside the
 * operator identity — not in the topbar.
 */
export function Sidebar() {
  const { collapsed } = useSidebarStore();
  const [prefsOpen, setPrefsOpen] = useState(false);

  const { data: runs = [] } = useQuery({
    queryKey: ['runs', { limit: 50 }],
    queryFn: () => api.listRuns({ limit: 50 }),
    refetchInterval: 5_000,
    staleTime: 4_000,
  });
  const hasActive = runs.some((r) => r.status === 'running');

  const operations: NavItem[] = [
    { to: '/',         label: 'Dashboard',    icon: 'grid' },
    { to: '/run',      label: 'Lancer',       icon: 'play' },
    { to: '/live',     label: 'Live',         icon: 'activity', badge: hasActive ? 'RUN' : undefined },
    { to: '/history',  label: 'Historique',   icon: 'history' },
  ];
  const surveillance: NavItem[] = [
    { to: '/sentinel', label: 'Sentinel',     icon: 'shield' },
  ];
  const catalogue: NavItem[] = [
    { to: '/tests',    label: 'Tests',        icon: 'flask' },
    { to: '/config',   label: 'Configuration', icon: 'sliders' },
    { to: '/suites',   label: 'Suites',       icon: 'grid' },
  ];
  const aide: NavItem[] = [
    { to: '/help',     label: 'Aide',         icon: 'book' },
  ];

  return (
    <>
      <aside className="side">
        {/* Brand — height 56 px, logo 40 px, platform name on 2 UPPERCASE
         * lines with the Lab module badge inline on the second (shorter) line. */}
        <div className="brand">
          <span className="logo-wrap">
            <Logo size={40} />
          </span>
          {!collapsed && (
            <div className="brand-text">
              <div className="brand-line">
                <span className="n">Automation</span>
              </div>
              <div className="brand-line">
                <span className="n">Platform</span>
                <span className="module-badge">Lab</span>
              </div>
            </div>
          )}
        </div>

        {!collapsed && <div className="group">Opérations</div>}
        {operations.map((it) => <NavItemRow key={it.to} item={it} collapsed={collapsed} />)}

        {!collapsed && <div className="group">Surveillance</div>}
        {surveillance.map((it) => (
          <NavItemRow key={it.to} item={it} trailing={<LED kind="ok" />} collapsed={collapsed} />
        ))}

        {!collapsed && <div className="group">Catalogue</div>}
        {catalogue.map((it) => <NavItemRow key={it.to} item={it} collapsed={collapsed} />)}

        {!collapsed && <div className="group">Aide</div>}
        {aide.map((it) => <NavItemRow key={it.to} item={it} collapsed={collapsed} />)}

        <div className="foot">
          <span className="av" title="Opérateur">OP</span>
          {!collapsed && (
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
              <span style={{ fontSize: 12, color: 'var(--text-primary)', fontWeight: 500, lineHeight: 1.1 }}>
                Opérateur
              </span>
              <span className="mono" style={{ fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.2, marginTop: 2 }}>
                automation
              </span>
            </div>
          )}
          <button
            type="button"
            onClick={() => setPrefsOpen(true)}
            className="settings-btn"
            aria-label="Paramètres de la plateforme"
            title="Paramètres de la plateforme"
          >
            <Icon name="settings" size={14} />
          </button>
        </div>
      </aside>

      <PreferencesModal open={prefsOpen} onClose={() => setPrefsOpen(false)} />
    </>
  );
}

function NavItemRow({
  item,
  trailing,
  collapsed,
}: {
  item: NavItem;
  trailing?: React.ReactNode;
  collapsed: boolean;
}) {
  const { setCollapsed } = useSidebarStore();
  // Sur mobile (≤ 480 px), cliquer un lien doit refermer le drawer.
  const closeIfMobile = () => {
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 480px)').matches) {
      setCollapsed(true);
    }
  };
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      onClick={closeIfMobile}
      className={({ isActive }) => cn('nav', isActive && 'act')}
      title={collapsed ? item.label : undefined}
    >
      <Icon name={item.icon} size={16} />
      <span>{item.label}</span>
      {item.badge && (
        <span className="badge run mono" style={{ marginLeft: 'auto', padding: '1px 6px' }}>
          <span className="dot" />{item.badge}
        </span>
      )}
      {item.count != null && !item.badge && <span className="c">{item.count}</span>}
      {!item.badge && item.count == null && trailing && (
        <span className="c">{trailing}</span>
      )}
    </NavLink>
  );
}
